import { apiSlice } from "./authApi";

export const patientVisitApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        // Fetch all visits for a specific patient by patient ID
        getVisitsByPatientId: builder.query({
            query: ({ patientId, page = 1, pageSize = 10 }) => ({
                url: `/patients/${patientId}/visits/`,
                method: "GET",
                params: { page, pageSize }, // Pass both page number and page size as query parameters
            }),
            providesTags: (result, error, { patientId }) => [
                { type: "PatientVisit", patientId },
            ],
            transformResponse: response => ({
                results: response.results,
                count: response.count
            }), // Optionally transform the response to only return results
            keepUnusedDataFor: 5, // Minutes to keep unused data, optional but useful for caching
        }),

        // Fetch a single visit by patient ID and visit ID
        getPatientVisit: builder.query({
            query: ({ patientId, visitId }) => ({
                url: `/patients/${patientId}/visits/${visitId}/`,
                method: "GET",
            }),
            providesTags: (result, error, { visitId }) => [
                { type: "PatientVisit", visitId },
            ],
        }),

        // Create a new visit for a specific patient by patient ID
        createPatientVisit: builder.mutation({
            query: ({ patientId, newVisit }) => ({
                url: `/patients/${patientId}/visits/`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: newVisit,
            }),
            invalidatesTags: (result, error, { patientId }) => [
                { type: "PatientVisit", patientId },
            ],
        }),

        // Update an existing visit by patient ID and visit ID
        updatePatientVisit: builder.mutation({
            query: ({ patientId, visitId, ...update }) => ({
                url: `/patients/${patientId}/visits/${visitId}/`,
                method: "PATCH",
                body: update,
                headers: {
                    "Content-Type": "application/json",
                },
            }),
            invalidatesTags: (result, error, { visitId }) => [
                { type: "PatientVisit", visitId },
            ],
        }),

        // Delete a visit by patient ID and visit ID
        deletePatientVisit: builder.mutation({
            query: ({ patientId, visitId }) => ({
                url: `/patients/${patientId}/visits/${visitId}/`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, { visitId }) => [
                { type: "PatientVisit", visitId },
            ],
        }),

        getVisitStats: builder.query({
            query: ({ timeframe }) => ({
                url: `/visit-stats/`,
                params: { timeframe },
            }),
            transformResponse: response => response,
        }),

        getAllVisits: builder.query({
            query: ({ page = 1, pageSize = 10 } = {}) => ({
              url: `visits/`,       
              params: { page, pageSize },
            }),
            transformResponse: response => ({
              results: response.results,
              count: response.count,
            }),
            providesTags: (result) =>
              result
                ? [
                    // tag each visit id for cache invalidation
                    ...result.results.map(({ id }) => ({ type: 'PatientVisit', id })),
                    { type: 'PatientVisit', id: 'LIST_ALL' },
                  ]
                : [{ type: 'PatientVisit', id: 'LIST_ALL' }],
          }),
        
          searchVisits: builder.query({
            query: ({ page = 1, pageSize = 10, search }) => ({
                url: `/visits/?page=${page}&pageSize=${pageSize}&search=${search}`,
                method: "GET",
            }),
            providesTags: (result) => {
                if (result?.results) {
                    return result.results.map((patient) => ({
                        type: "PatientVisit",
                        id: patient.id,
                    }));
                }
                return [{ type: "PatientVisit", id: "SEARCH" }];
            },
        }),
    }),
});

export const {
    useGetVisitsByPatientIdQuery,
    useGetPatientVisitQuery,
    useCreatePatientVisitMutation,
    useUpdatePatientVisitMutation,
    useDeletePatientVisitMutation,
    useGetVisitStatsQuery,
    useGetAllVisitsQuery,
    useSearchVisitsQuery
} = patientVisitApiSlice;
